// src/core/composables/useAuthApi.ts
import { ref } from "vue";
import type { Auth } from "@/models";
import { auth } from "@/core/store/auth";
import { useAuthToken } from "@/core/composables/useAuthToken";
import { authService } from "@/core/services/auth.service";
import { createApiComposable } from "@/core/composables/createApiComposable";

function computeExpirationFromEnvHours(): Date {
  const hoursRaw = import.meta.env.VITE_APP_TOKEN_HOURS;
  const hours = Number(hoursRaw);
  const safeHours = Number.isFinite(hours) && hours > 0 ? hours : 1;

  const expiration = new Date();
  expiration.setHours(expiration.getHours() + safeHours);
  return expiration;
}

export function useAuthApi() {
  const newToken = ref("");

  const { hasValidToken, setToken, clearIfExpired } = useAuthToken();

  // const { errorToast, successToast } = useAlerts();
  const api = createApiComposable({
    defaultErrorMessage: "Request failed",
  });

  const fetchToken = async (body: Auth) => {
    const data = await api.run(() => authService.fetchToken(body), "Failed to fetch token");

    newToken.value = data.token;

    const expiration = computeExpirationFromEnvHours();
    setToken(data.token, expiration);

    return data.token;
  };

  const refreshTokenIfNeeded = async () => {
    clearIfExpired();
    if (hasValidToken.value) return;

    const accountId = auth.account?.localAccountId;
    if (!accountId) {
      api.clearError();
      (api.error as any).value = "No authenticated account found"; // if you prefer, expose setError in createApiComposable
      return;
    }

    await fetchToken({ id: accountId });
  };

  return {
    newToken,

    loading: api.loading,
    error: api.error,
    clearError: api.clearError,

    fetchToken,
    refreshTokenIfNeeded,
  };
}
